import { z } from "zod";
import type { ImageReference } from "./images";
import { zMotionEasing } from "./stageMovement";
import { itemImagePanels } from "./itemPresentation";

const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
const finite = z.number().finite();
export const zPropPoint = z.object({ x: finite.min(-2).max(3), y: finite.min(-2).max(3) }).strict();
/** Story snapshots retain the complete account artwork reference. */
export type ItemVisual = ImageReference;

/** Props are a staging use of an Item. Their motion never changes possession. */
export const zStoryPropCue = z.object({
  kind: z.literal("prop"),
  itemId: id,
  /** A named onstage copy of this Item; omission keeps the original single prop. */
  instanceId: id.optional(),
  state: z.enum(["show", "hide"]).optional(),
  variant: id.nullable().optional(),
  /** Centre in artwork fractions; offscreen coordinates permit entrances/exits. */
  at: zPropPoint.optional(),
  /** Width as a fraction of the artwork width, retaining the panel's aspect. */
  width: finite.min(0.01).max(3).optional(),
  rotation: finite.min(-720).max(720).optional(),
  /** Starting angle for this movement; otherwise continue the previous angle or start at zero. */
  fromRotation: finite.min(-720).max(720).optional(),
  layer: z.enum(["scene", "foreground"]).optional(),
  from: zPropPoint.optional(),
  via: z.array(zPropPoint).max(24).optional(),
  style: z.enum(["slide", "path"]).optional(),
  /** Omitted preserves linear playback for existing scores. */
  easing: zMotionEasing.optional(),
  /** Continuous, transport-clock movement for a silent companion or object. */
  groove: z.enum(["bop", "sway", "none"]).optional(),
  bpm: z.number().int().min(40).max(240).optional(),
});
export type StoryPropCue = z.infer<typeof zStoryPropCue>;

export function propInstanceKey(prop: { itemId: string; instanceId?: string }): string {
  return prop.instanceId ? `${prop.itemId}:${prop.instanceId}` : prop.itemId;
}

export function itemVisualFromImage(image: ImageReference | undefined): ImageReference | undefined {
  return image;
}

/** Small text-only description for generation; never a persisted artwork substitute. */
export function storyPropArtwork(image: ImageReference) {
  const panels = itemImagePanels(image);
  return { variants: panels.map((panel) => panel.name), defaultVariant: image.selectedPanel?.name || panels[0]?.name };
}
